import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";

import Button from "@material-ui/core/Button";
import WarningIcon from "@material-ui/icons/Warning";

import { useClickBlockerContext } from "components/BlockableClickContext";
import Loader from "components/Loader";
import Textarea from "components/Textarea";
import UnsavedChangesDialog from "components/UnsavedChangesDialog";
import { useRacehorse360Api } from "hooks/api";

import useStyles from "./styles";

interface IProps {
  horseId?: string;
  commentId?: string;
  comment?: string;
  warningComment?: string;
  className?: string;
  refetchHorseWarningMessage: () => void;
  refetchComments: () => void;
  onClose: () => void;
}

const HorseGeneralVetCommentEditor = (props: IProps) => {
  const {
    horseId,
    commentId,
    className,
    refetchHorseWarningMessage,
    refetchComments,
    onClose
  } = props;

  const classes = useStyles();
  const { setBlockClick } = useClickBlockerContext();
  const commentInputRef = useRef<HTMLInputElement>(null);
  const warningCommentInputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();

  const comment = useRef<string>(props.comment);
  const warningComment = useRef<string>(props.warningComment);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [linkCandidate, setLinkCandidate] = useState<string>(null);
  const [showWarning, setShowWarning] = useState(
    Boolean(warningComment.current)
  );
  const [isCommentChanged, setIsCommentChanged] = useState(false);
  const [isWarningChanged, setIsWarningChanged] = useState(false);

  const { useCreateHorseGeneralVetComment, useUpdateHorseGeneralVetComment } =
    useRacehorse360Api();

  const {
    mutateAsync: createHorseGeneralVetComment,
    isPending: isLoadingCreateHorseGeneralVetComment
  } = useCreateHorseGeneralVetComment({
    onError: error => {
      console.error(error);
    }
  });

  const {
    mutateAsync: updateHorseGeneralVetComment,
    isPending: isLoadingUpdateHorseGeneralVetComment
  } = useUpdateHorseGeneralVetComment({
    onError: error => {
      console.error(error);
    }
  });

  useEffect(() => {
    const input = warningCommentInputRef.current;
    if (showWarning && input) {
      input?.focus();
      input.selectionStart = input.value.length;
    }
  }, [showWarning]);

  useEffect(() => {
    const input = commentInputRef.current;
    if (!showUnsavedChangesDialog && input) {
      input?.focus();
      input.selectionStart = input.value.length;
    }
  }, [showUnsavedChangesDialog]);

  const redirectToLinkCandidate = () => {
    if (linkCandidate === "backLink") {
      history.goBack();
    } else {
      history.push(linkCandidate);
    }
  };

  const unlockNavigation = () => {
    setBlockClick(false);
    setShowUnsavedChangesDialog(false);
  };

  const handleBlur = e => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (!isCommentChanged && !isWarningChanged) {
        onClose();

        return;
      }

      const path = e.relatedTarget?.dataset?.path;

      setBlockClick(true);
      setShowUnsavedChangesDialog(true);
      path && setLinkCandidate(path);
    }
  };

  const handleChangeComment = value => {
    comment.current = value;
    setIsCommentChanged(value?.trim().localeCompare(props.comment) !== 0);
  };

  const handleChangeWarningComment = value => {
    warningComment.current = value;
    setIsCommentChanged(
      value?.trim().localeCompare(props.warningComment) !== 0
    );
  };

  const handleCancelComment = () => {
    unlockNavigation();
    onClose();
  };

  const handleSaveComment = async () => {
    if (!isCommentChanged && !isWarningChanged) return;

    if (commentId) {
      await updateHorseGeneralVetComment({
        horseGeneralVetComment: {
          id: commentId,
          body: comment.current?.trim(),
          warningMessage: warningComment.current?.trim()
        },
        updateMask: {
          paths: ["body", "warningMessage"]
        }
      });
    } else {
      await createHorseGeneralVetComment({
        horseGeneralVetComment: {
          horseId: horseId,
          body: comment.current.trim(),
          warningMessage: warningComment.current?.trim()
        }
      });
    }

    if (linkCandidate) {
      redirectToLinkCandidate();
    } else {
      isWarningChanged && refetchHorseWarningMessage();
      refetchComments();
      onClose();
    }
  };

  const handleCancelUnsavedChanges = () => {
    setLinkCandidate(null);
    setShowUnsavedChangesDialog(false);
  };

  const handleDiscardChanges = () => {
    unlockNavigation();

    if (linkCandidate) {
      redirectToLinkCandidate();
    } else {
      onClose();
    }
  };

  const handleWarningButtonClick = () => {
    if (showWarning) {
      commentInputRef.current?.focus();
      warningComment.current = null;
    }
    setShowWarning(!showWarning);
    setIsWarningChanged(true);
  };

  const renderUnsavedChangesPopupContent = () => {
    return (
      <>
        <span className={classes.warningText}>
          Your comment has not been saved.
        </span>
        <span className={classes.warningText}>
          Are you sure you want to leave and discard your comment?
        </span>
      </>
    );
  };

  const renderContent = () => {
    if (
      isLoadingCreateHorseGeneralVetComment ||
      isLoadingUpdateHorseGeneralVetComment
    ) {
      return <Loader className={classes.loader} />;
    }

    return (
      <div
        className={clsx(classes.root, className)}
        tabIndex={0}
        onBlur={handleBlur}
      >
        <Textarea
          className={classes.commentTextarea}
          inputRef={commentInputRef}
          maxLength={500}
          label="General Comment"
          rows={8}
          defaultValue={comment.current || ""}
          onChange={handleChangeComment}
        />
        {showWarning && (
          <Textarea
            className={classes.commentTextarea}
            inputRef={warningCommentInputRef}
            maxLength={60}
            label="Warning Comment"
            rows={1}
            defaultValue={warningComment.current || ""}
            onChange={handleChangeWarningComment}
          />
        )}
        <div className={classes.buttons}>
          <Button
            className={clsx(classes.button, classes.warningButton, {
              active: showWarning
            })}
            startIcon={<WarningIcon />}
            onClick={handleWarningButtonClick}
          >
            {showWarning ? "REMOVE WARNING" : "ADD WARNING"}
          </Button>
          <div className={classes.controlButtons}>
            <Button
              className={classes.button}
              onMouseDown={handleCancelComment}
            >
              Cancel
            </Button>
            <Button
              className={clsx(classes.button, classes.saveButton)}
              disabled={!isCommentChanged && !isWarningChanged}
              onMouseDown={handleSaveComment}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderContent()}

      {showUnsavedChangesDialog && (
        <UnsavedChangesDialog
          open
          title={"Discard Comment?"}
          content={renderUnsavedChangesPopupContent()}
          isSaveDisabled
          onSave={handleSaveComment}
          onCancel={handleCancelUnsavedChanges}
          onDiscard={handleDiscardChanges}
        />
      )}
    </>
  );
};

export default React.memo(HorseGeneralVetCommentEditor);
